import { useCallback, useContext, useEffect, useState } from 'react';
import { isApolloError } from 'apollo-client';
import { get } from 'lodash';
import gql from 'graphql-tag';
import tokenStore from 'MoshtixShared/helper-token-store';
import configuration from 'Configuration/index';
import { TicketDistroContext } from '../../app/contexts';
import { useMutation } from '../helper-apollo-hooks';

const GENERIC_ERROR_MESSAGE =
  'Something’s up. A team of highly trained monkeys have been dispatched to deal with the problem.';
const INVALID_TOKEN_MESSAGE = 'Access token is invalid';
const SECURITY_TOKEN_INVALID_MESSAGE = 'security token included in the request is invalid';
const TICKET_NO_LONGER_AVAILABLE_MESSAGE = 'Ticket no longer available';
const REDIRECT_URL = `//${configuration.publicWebsiteOriginalUrl}/v2/account/login`;

type LoginVariables = {
  accessToken?: string;
};

export type UseToLoginConfig = {
  params: LoginVariables;
  treatInvalidTokenAsUnavailable?: boolean;
};

type LoginResult = { login: string };

const loginWithAccessToken = gql`
  mutation login($accessToken: String) {
    login(accessToken: $accessToken, isCustomer: true)
  }
`;

const { cookieDomain } = configuration;
const clearCookies = (): void => {
  tokenStore.clearTicketDistroToken(cookieDomain);
  tokenStore.clearAccessToken(cookieDomain);
};

const useToLogin = ({ params = {}, treatInvalidTokenAsUnavailable = false }: UseToLoginConfig): boolean => {
  const { ticketDistroContext }: any = useContext(TicketDistroContext);
  const [loginMutation, { error }] = useMutation<LoginVariables, LoginResult>(loginWithAccessToken);
  const [lastAttemptedToken, setLastAttemptedToken] = useState<string | undefined>(undefined);

  // Extract setState to avoid reference instability in dependency arrays
  const setDistroState = get(ticketDistroContext, 'actions.setState');

  const currentToken = params.accessToken || tokenStore.getAccessToken();
  const tokenHasChanged = currentToken !== lastAttemptedToken;

  const shouldTryToLogin =
    !ticketDistroContext.state.isLoggedIn &&
    !ticketDistroContext.state.isLoggingIn &&
    (!error || tokenHasChanged) &&
    !ticketDistroContext.state.ticketNoLongerAvailable;

  useEffect(() => {
    if (error && isApolloError(error)) {
      const ticketNoLongerAvailable = error.message.indexOf(TICKET_NO_LONGER_AVAILABLE_MESSAGE) > -1;
      const invalidToken = error.message.indexOf(INVALID_TOKEN_MESSAGE) > -1;
      const securityTokenInvalid =
        error.message.toLowerCase().indexOf(SECURITY_TOKEN_INVALID_MESSAGE.toLowerCase()) > -1;

      if (ticketNoLongerAvailable || (treatInvalidTokenAsUnavailable && (invalidToken || securityTokenInvalid))) {
        setDistroState({ ticketNoLongerAvailable: true, isLoggingIn: false });
        return;
      }

      if (!treatInvalidTokenAsUnavailable && (invalidToken || securityTokenInvalid)) {
        clearCookies();
        document.location.href = REDIRECT_URL;
        return;
      }

      const genericError = error.message.indexOf(GENERIC_ERROR_MESSAGE) > -1;
      if (genericError) {
        clearCookies();
        document.location.href = REDIRECT_URL;
      }
    }
  }, [error, treatInvalidTokenAsUnavailable, setDistroState]);

  const login = useCallback(
    async (accessToken: string | undefined) => {
      if (ticketDistroContext.state.isLoggingIn) {
        return;
      }

      // Track the token we're attempting to prevent sticky error blocking retry
      setLastAttemptedToken(accessToken);

      // since the logged in state is global,
      // this needs to be global, to avoid multiple login calls
      setDistroState({ isLoggingIn: true });

      clearCookies();

      try {
        const { data } = await loginMutation({
          variables: { accessToken },
        });

        const token = get(data, 'login');

        if (token) {
          // save the new token
          // (for the cases when the user goes straight to the page)
          tokenStore.setTicketDistroToken({ token, domain: cookieDomain });

          // if there is a token, save the global state as logged in and reset logging in state
          setDistroState({ isLoggedIn: true, isLoggingIn: false });
        } else {
          // No token returned - reset logging in state
          setDistroState({ isLoggingIn: false });
        }

        if (accessToken) {
          // save the access token in the cookies
          // (for the cases when the user goes straight to the page)
          tokenStore.setAccessToken({ token: accessToken, domain: cookieDomain });
        }
      } catch (err) {
        setDistroState({ isLoggingIn: false });
      }
    },
    [loginMutation, ticketDistroContext.state.isLoggingIn, setDistroState],
  );

  useEffect(() => {
    // If we have an access token in params from URL, use it exclusively
    // Don't fall back to cached token when a new token is provided via URL
    const accessToken = params.accessToken || tokenStore.getAccessToken(); // Only fallback if no URL token

    if (shouldTryToLogin && accessToken) {
      login(accessToken);
    }
  }, [params, loginMutation, shouldTryToLogin, login]);

  return ticketDistroContext.state.isLoggedIn;
};

export default useToLogin;
